/**
 * "You are looking at an old version" — §2.8 #100, #112.
 *
 * Deduped from two places that said the same thing differently:
 * `SourcesPanel` rendered it where the Add Source button goes, and
 * `StagedActionBar` rendered it in place of both its idle and dirty states.
 * The two had already drifted — one said "to add or edit sources", the other
 * "to make changes", one had a lock icon and the other did not — which is the
 * usual way a duplicated notice becomes two different claims about one rule.
 *
 * The rule itself: staging only ever happens against the LATEST version.
 * Versions are immutable, so editing a past one is not a thing that could be
 * made to work — the notice has to say where to go, not just that the controls
 * are gone.
 *
 * `latestVersion` is optional because the version list loads in parallel. With
 * it, the copy names the destination; without it, it stays truthful and vague
 * rather than rendering `vundefined`.
 */
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PastVersionNoticeProps {
  /** The version being viewed. */
  currentVersion: string;
  /** The version to switch back to, once the version list has resolved. */
  latestVersion?: string;
  /**
   * `panel` — boxed, with a lock icon, for the Add Source slot.
   * `bar` — a bare line for the staged action bar, which has its own border.
   */
  variant?: 'panel' | 'bar';
  /** What switching back would let the user do. */
  action?: string;
}

export function PastVersionNotice({
  currentVersion,
  latestVersion,
  variant = 'panel',
  action = 'make changes',
}: PastVersionNoticeProps) {
  const body = (
    <span className='leading-snug'>
      Viewing{' '}
      <span className='font-mono font-medium text-foreground'>
        v{currentVersion}
      </span>
      .{' '}
      {latestVersion ? (
        <>
          Switch to{' '}
          <span className='font-mono font-medium text-foreground'>
            v{latestVersion}
          </span>{' '}
          to {action}.
        </>
      ) : (
        `Switch to the latest version to ${action}.`
      )}
    </span>
  );

  if (variant === 'bar') {
    return (
      <div className='border-t border-border/60 bg-transparent px-4 py-2.5'>
        <p className='text-xs text-muted-foreground'>{body}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-border/60 bg-gray-50/50 px-3 py-2 text-xs text-muted-foreground dark:bg-gray-900/30'
      )}
    >
      <Lock className='mt-0.5 h-3 w-3 flex-shrink-0' aria-hidden='true' />
      {body}
    </div>
  );
}
